import ContextMenuController from "./context_menu_controller"
import { Turbo } from "@hotwired/turbo-rails"

// Right-click context menu for an editable canvas element on the deck SHOW
// page (card #2079 Phase 1 follow-up, deck #28 incident, second round).
// Subclasses the base context-menu primitive exactly like
// image_context_menu_controller.js: the base owns contextmenu interception,
// viewport-aware positioning, the open/close lifecycle, and Turbo-frame
// loading (UiHelper#context_menu renders the shared shell — see
// decks/_viewer.html.erb). This controller supplies which element was
// right-clicked, the frame endpoint (DecksController#element_menu), and the
// two menu actions.
//
// SUPERSEDES a same-day first pass that bound deck_show_editor_controller.js
// straight onto `contextmenu`, jumping into edit mode immediately. Jay tested
// that live and called it wrong: a right-click must show a MENU first, with
// Edit as one option among others, never fire an action automatically. This
// controller is that menu. Choosing "Edit" hands off to
// deck_show_editor_controller.js's UNCHANGED openEditorFor(target) — the
// textarea/commit/blank-guard logic there is untouched, only its entry point
// moved from a direct DOM binding to this menu selection.
//
// Choosing "Add Feedback" swaps the SAME positioned panel to a small composer
// (element_feedback_composer partial) and posts straight into
// DeckSlideFeedbacksController#create — the exact model/thread the deck's
// Feedback nav tab already reads and writes (DeckSlideFeedback, slide-scoped).
// No parallel feedback store: the response is the SAME turbo_stream that
// controller already renders (replacing the — currently off-screen, flipped —
// comment thread), applied here via Turbo.renderStreamMessage, the same
// fetch-and-apply pattern runbook_graph_controller.js already uses elsewhere
// in the app for a plain POST + turbo-stream response.
//
// TWO ROUNDS OF LIVE-TESTING FIXES (same day) on top of the above:
//   1. Width/position: the shell's width class used to be narrower (w-52)
//      than the composer's own hardcoded content width, and the shell clips
//      overflow — Jay caught the Post button rendering as a clipped sliver.
//      Fixed by making the composer fill whatever width the shell is
//      configured with (no more hardcoded mismatch) and re-clamping position
//      on every frame load (handleFrameLoad), not just once at open() time.
//   2. Dismissal: the composer used to inherit the base menu's click-away/
//      right-click-elsewhere auto-close, risking a lost draft from one stray
//      click. disableClickAwayDismiss() detaches ONLY that (Escape stays
//      attached), and an explicit X/Cancel are the deliberate close paths.
//      A draft is also persisted to localStorage per (deck, slide) as the
//      user types, restored next time that slide's composer opens, and
//      cleared only on a successful post — see _wireComposer / _saveDraft.
//
// THIRD ROUND — on-slide pins: choosing "Add Feedback" now also captures the
// ORIGINAL right-click's position as a percentage of the slide canvas
// (_capturePinPosition) and posts it alongside the comment, so
// decks/_slide_feedback_pins.html.erb can render a small dot exactly where
// the reviewer right-clicked. The resolved toggle inside that pin's hover
// popover, and the one in the Feedback-tab thread list, are the SAME server
// partial (decks/_slide_feedback_resolve_toggle) posting straight to
// DeckSlideFeedbacksController#update — no JS needed for that action at all,
// it's a plain Turbo button_to.
//
// FOURTH ROUND (card #2378, deck #42 Yarra Valley Hilltop incident) — element
// attribution. A slide with several stat tiles left a reviewer's comment
// ambiguous about which tile it was about. _capturePinPosition now ALSO
// resolves the nearest slide element to that same click (_captureElementSnapshot)
// and snapshots its rendered content at that moment, posting both alongside
// the existing pin position — see that method's own header comment for the
// resolution order (direct hit, then a small-radius elementFromPoint fallback).
// FIFTH ROUND (card #2834) — right-click stops working after a deck-level or
// deck_slide-level broadcast. Root cause: AiTools::BaseService#broadcast_slide_refresh!
// and #broadcast_deck_refresh! ALWAYS render their replacement partials with
// `editable: false` (a server push has no per-tab request context, so it can't
// know which viewing tab has update rights — see those methods' own comments in
// base_service.rb). DecksHelper#deck_element_edit_attrs / #deck_element_menu_image_attrs,
// and the per-slide fallback action in decks/_slide_list.html.erb, are ALL gated
// on `editable` — so an `editable: false` render simply OMITS data-slide-index /
// data-element-index / data-action from the DOM entirely. Not stale, never
// rendered. A full page reload "fixes" it only because DecksController#show
// recomputes `editable` correctly per-request for that specific user.
//
// Passing `editable: true` into the broadcast is NOT an option: `turbo_stream_from
// "deck_#{id}"` broadcasts the SAME html to every subscriber, including a
// non-editor reviewer viewing the same show page — that would leak edit
// affordances to a user without update rights.
//
// The fix instead is client-side reattachment, gated by this controller's own
// presence: DeckElementContextMenuController is ONLY ever attached (see
// decks/_viewer.html.erb) when `editable` was true at THIS TAB's own initial
// page render. If this controller is connected at all, this tab is editable,
// full stop — no new permission check needed. Same `turbo:before-stream-render`
// wrap-the-default-render pattern airdrop_report_viewer_controller.js already
// uses on this same page (for slide entrance/exit animation) — see that
// controller's handleBeforeStreamRender for the precedent. Deliberately NOT
// narrow-scoped to one stream target the way that one is: both
// broadcast_deck_refresh! (whole-list replace, target "deck_#{id}_slides") and
// broadcast_slide_refresh! (single-slide replace, target Deck.slide_frame_id)
// can strip these attributes, and re-scanning is cheap and idempotent (a no-op
// once everything's already correctly attributed), so there's no need to branch
// by which one just fired.
//
// SCOPE (deliberately narrow): only restores what the show-page right-click
// menu itself needs to resolve a click — data-slide-index, data-element-index,
// and the contextmenu action. Does NOT reconstruct the richer
// deck_element_edit_attrs output (data-element-kind, data-element-id,
// data-override-reason, the hovercard wiring, data-deck-editable, the
// override-lock badge) — those ALSO vanish on broadcast, but restoring them
// is a separate, undiscovered gap outside this card's scope (e.g.
// deck_show_editor_controller.js's openEditorFor degrades gracefully without
// elementKind — it just loses the headline "snug" sizing and falls back to the
// text/html minimum-size layout).
//
// SEVENTH ROUND (card #3068 follow-up) — the native table kind (card #3068)
// shipped weeks after this allowlist was written, and ".airdrop-el--table"
// was never added to it. Every OTHER editable-only kind (text/stat/image)
// survived a broadcast's reattachment correctly; a table element's own
// "Edit table" right-click option simply never came back after the FIRST
// live cell edit (which itself triggers a broadcast_slide_refresh!) — an
// omission, not a deliberate exclusion, unlike shape/html/analytics below
// (those never had a contextmenu action even in editable mode, see
// reattachElementActions' own comment).
const RESTORABLE_ELEMENT_SELECTOR = ".airdrop-el--text, .airdrop-el--stat, .airdrop-el--image, .airdrop-el--image-crop, .airdrop-el--table"

// SIXTH ROUND (card #2903) — right-click resolves the WRONG element after a
// broadcast, on a slide that was never even touched by it. A same-day first
// pass elsewhere on this card added a `@stale` fallback to
// DecksController#element_menu on a concurrent-edit theory; that theory was
// WRONG and is left untouched here (still valid defensive behaviour, just not
// the cause of THIS bug — see that controller's own comment).
//
// Real root cause: reattachElementActions (below) used to derive elementIndex
// from canvasRoot.children's raw DOM position. But decks/slides/_canvas.html.erb's
// element loop ALSO renders up to two more sibling nodes per element —
// DecksHelper#deck_override_lock_badge (an amber lock badge span, ADR-0094)
// and #deck_metric_disclosure_icon (a blue info-circle span, ADR-0107) — plus
// one more once at the end (decks/_slide_feedback_pins) — none of which
// correspond to an elements[] entry. Counting those as if they were elements
// shifts every LATER element's recomputed index by however many sibling nodes
// sit before it. Since reattachElementActions re-scans EVERY canvas on the
// page on EVERY stream render, not just the one a given broadcast targeted
// (see handleBeforeStreamRender's own comment), this corrupts a slide's
// indices even when that slide itself was never replaced — right-clicking
// element B then silently opens element C's Edit/Add Feedback, a wrong-
// attribution bug worse than the "menu doesn't open" bug #2834 originally
// fixed.
//
// Fix: exclude these three known non-element sibling node shapes before
// indexing, rather than counting raw children. Deliberately an EXCLUDE list
// (not "require .airdrop-el") — an "analytics" kind element's iframe
// (_canvas.html.erb's analytics branch) renders as a genuine elements[] entry
// but does NOT carry the .airdrop-el class server-side (unlike its JS-editor-
// preview twin, deck_editor_controller.js#elementNode, which does) — a
// positive `.airdrop-el` requirement would silently drop it from the count
// and reintroduce this exact class of bug for any slide with an analytics
// element followed by another element.
const NON_ELEMENT_CANVAS_SIBLING_SELECTOR = ".deck-show-editor-lock, .deck-metric-disclosure, [id*='_slide_feedback_pins_']"

// SEVENTH ROUND (card #2903 Bug 2) — reattachElementActions still shifted
// indices on any slide with an "html" kind element (an embedded iframe/table
// block), even after the sixth round's fix above. FIDO reproduced live: after
// one ordinary broadcast, 47 of 648 checked elements across the deck were
// mis-indexed, on every slide containing one of these.
//
// Root cause: decks/slides/_canvas.html.erb's "html" branch (the un-overridden
// case — no `_manual_overrides` entry for this element) renders TWO direct
// canvas-root children for the ONE elements[] entry: the content
// `<iframe class="airdrop-el airdrop-el--html">` itself, PLUS — only when
// `edit_attrs` is present, i.e. only in a render where THIS specific slide's
// canvas was editable — an invisible, identically-geometried
// `<div class="airdrop-el">` overlay carrying the real edit_attrs (data-
// element-index/data-action). The overlay exists at all ONLY because a
// sandboxed `<iframe srcdoc>` is a separate browsing context — a contextmenu
// event firing INSIDE it can never bubble up to the iframe element in the
// PARENT document, so the iframe itself can never carry the click target (see
// that partial's own comment on the overlay, right above where it's
// rendered). Treating the iframe and its overlay as two separate elements
// counts one logical entry twice, shifting every LATER element's recomputed
// index by one — the exact same shape of bug the sixth round fixed for the
// lock badge / metric icon / feedback pins, just for a sibling pair instead
// of a sibling extra.
//
// NOT solved by adding `iframe.airdrop-el--html` to the plain exclude list
// above: the override case (`html_override` present) renders a DIFFERENT
// shape — ONE plain `<div class="airdrop-el airdrop-el--html">` carrying
// edit_attrs directly, no iframe, no separate overlay, no pairing at all —
// excluding every `.airdrop-el--html` unconditionally would silently drop
// THAT div from the count instead, reintroducing the exact same off-by-one in
// the opposite direction. Likewise, an un-overridden html element rendered in
// a NON-editable pass (e.g. this specific slide's own canvas WAS just
// broadcast-replaced, which always renders `editable: false` — see this
// file's SIXTH ROUND comment) has no overlay at all: just the bare iframe,
// which correctly IS the one counted slot for that entry in that render.
// Only the iframe-directly-followed-by-its-own-overlay shape is ever a pair;
// every other shape (bare iframe alone, or the override div) is already
// exactly one node per entry and needs no collapsing.
//
// Selector is tag-qualified (`iframe.airdrop-el--html`, not a bare
// `.airdrop-el--html`) so it only ever matches the un-overridden content
// iframe, never the override div above (a `<div>`, same class, different
// tag).
const HTML_CONTENT_IFRAME_SELECTOR = "iframe.airdrop-el--html"

export default class extends ContextMenuController {
  static targets = ["menu", "frame", "loading", "content", "feedbackBody"]
  static values = { deckId: Number }

  connect() {
    super.connect()
    this.boundHandleBeforeStreamRender = (event) => this.handleBeforeStreamRender(event)
    document.addEventListener("turbo:before-stream-render", this.boundHandleBeforeStreamRender)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.boundHandleBeforeStreamRender)
    super.disconnect()
  }

  // Not narrow-scoped by stream target (see header comment) — every stream
  // render on the page gets wrapped, and reattachEditAttrs re-scans THIS
  // controller's own root (this.element, the outer .airdrop-viewer wrapper —
  // never itself replaced by either broadcast) after the default render
  // completes. Cheap even on an unrelated stream (a card activity, a chat
  // message) since it's a handful of scoped querySelectorAll calls.
  handleBeforeStreamRender(event) {
    const defaultRender = event.detail.render
    event.detail.render = async (streamElement) => {
      await defaultRender(streamElement)
      this.reattachEditAttrs()
    }
  }

  reattachEditAttrs() {
    this.reattachSlideFallbackActions()
    this.reattachElementActions()
  }

  // Per-slide fallback (empty-slide-space right-click) — decks/_slide_list.html.erb
  // line ~186 attaches this to `.airdrop-slide-scale` when editable. `slide_index`
  // is re-derived from the slide wrapper's OWN `id="slide-N"` (1-based, ALWAYS
  // present regardless of `editable` — see that partial's own id attribute),
  // converted back to the 0-based index the route/menu expect.
  reattachSlideFallbackActions() {
    this.element.querySelectorAll(".airdrop-viewer__slide").forEach((slideEl) => {
      const match = slideEl.id.match(/^slide-(\d+)$/)
      if (!match) return
      const slideIndex = parseInt(match[1], 10) - 1

      const scaleEl = slideEl.querySelector(".airdrop-slide-scale")
      if (!scaleEl) return
      scaleEl.dataset.slideIndex = String(slideIndex)
      scaleEl.setAttribute("data-action", "contextmenu->deck-element-context-menu#open")
    })
  }

  // Per-element — within each slide's `.airdrop-slide.airdrop-slide--canvas`
  // root, `element_index` is the element's position among that root's DIRECT
  // children that actually represent an `elements[]` entry (decks/slides/
  // _canvas.html.erb's `elements.each_with_index` loop renders exactly one
  // direct-child DOM node per element, in array order, with no conditional
  // skip — a link-wrapped element (`options.link`) still counts as ONE node,
  // since the `<a>` wrapper itself is display:contents and is the thing
  // sitting at that position, not the element inside it).
  //
  // NOT simply canvasRoot.children's raw position (card #2903 fix — see
  // NON_ELEMENT_CANVAS_SIBLING_SELECTOR's own comment above): that same loop
  // ALSO interleaves up to two more sibling nodes per element (the override
  // lock badge, the metric disclosure icon) plus one more once at the end
  // (the feedback pins container), none of which correspond to an elements[]
  // entry. Filtering those out before indexing is what keeps a later
  // element's recomputed index matching its true array position, regardless
  // of how many such sibling nodes sit before it.
  //
  // Restricted to RESTORABLE_ELEMENT_SELECTOR (text/headline/stat/image, plus
  // table via deck_element_menu_image_attrs — card #3068's own contextmenu-only,
  // never click-to-edit, wiring — see _canvas.html.erb's "table" branch),
  // NOT every direct child — a shape, distribution-chart, or analytics
  // element NEVER carries edit_attrs OR menu attrs, even when editable (see
  // DecksHelper#deck_element_edit_attrs and _canvas.html.erb's shape/distribution_chart/
  // analytics branches, which skip the call entirely). An "html" element IS
  // eligible for edit_attrs (DecksHelper#deck_element_editable? explicitly
  // lists it) — but the node that carries them is its OVERLAY div (see
  // HTML_CONTENT_IFRAME_SELECTOR's own comment above), which is a bare,
  // classless `.airdrop-el` RESTORABLE_ELEMENT_SELECTOR doesn't match either
  // — so the practical effect is the same as shape/chart/analytics: no
  // data-action gets (re)attached to it today. Attaching one to any of these
  // unconditionally would be a behaviour CHANGE, not a restoration: today,
  // right-clicking one of those bubbles past it (no data-action of its own)
  // up to the slide-level fallback above, resolving as "Add Feedback"
  // attributed to the SLIDE. Giving it its own data-element-index would
  // instead resolve to that specific (contentless, for shape/chart/analytics)
  // element — a different, narrower attribution than editable mode has ever
  // produced. It still MUST be counted toward elementIndex, though — the
  // filter below only excludes the three known NON-element siblings (plus,
  // per card #2903 Bug 2, collapses an html element's iframe+overlay PAIR
  // into the one slot it represents), so a shape/chart/html/analytics node
  // still occupies exactly one position in the count even though
  // `_restorableTargetFor` then declines to give most of them a data-action.
  reattachElementActions() {
    this.element.querySelectorAll(".airdrop-slide--canvas").forEach((canvasRoot) => {
      const slideIndex = this._slideIndexForCanvas(canvasRoot)
      if (slideIndex === null) return

      const rawChildren = Array.from(canvasRoot.children).filter(
        (child) => !child.matches(NON_ELEMENT_CANVAS_SIBLING_SELECTOR)
      )

      // Card #2903 Bug 2: collapse an html element's content iframe + its
      // own overlay (see HTML_CONTENT_IFRAME_SELECTOR's header comment) into
      // the ONE slot they jointly represent, by dropping the iframe from the
      // counted sequence whenever the very next sibling is that same
      // element's overlay. Every other shape on this list (a bare iframe
      // with no overlay following it, the override div, or any other kind
      // entirely) already occupies exactly one node and passes through
      // unchanged.
      const elementNodes = rawChildren.filter((child, i) => {
        const next = rawChildren[i + 1]
        return !(child.matches(HTML_CONTENT_IFRAME_SELECTOR) && next && this._isHtmlOverlayFor(next))
      })

      elementNodes.forEach((child, elementIndex) => {
        const target = this._restorableTargetFor(child)
        if (!target) return

        target.dataset.slideIndex = String(slideIndex)
        target.dataset.elementIndex = String(elementIndex)
        target.setAttribute("data-action", "contextmenu->deck-element-context-menu#open")
      })
    })
  }

  // A "next sibling" only ever pairs with the content iframe right before it
  // when it's that overlay div: decks/slides/_canvas.html.erb renders it as a
  // bare `<div class="airdrop-el">` — no kind suffix, no other class at all
  // (unlike every other canvas element, which always carries at least one
  // `.airdrop-el--*` modifier) — immediately after the iframe, with nothing
  // else in between. Checking the FULL class list (not just "contains
  // airdrop-el") is deliberate: a future element kind that happens to add
  // `.airdrop-el` plus some other class of its own must never be
  // misidentified as this pairing.
  _isHtmlOverlayFor(el) {
    return el.classList.length === 1 && el.classList.contains("airdrop-el")
  }

  // Same id="slide-N" convention reattachSlideFallbackActions reads above —
  // the canvas root's own ancestor wrapper.
  _slideIndexForCanvas(canvasRoot) {
    const slideEl = canvasRoot.closest(".airdrop-viewer__slide")
    const match = slideEl?.id.match(/^slide-(\d+)$/)
    return match ? parseInt(match[1], 10) - 1 : null
  }

  // `child` is a direct child of the canvas root — either the restorable
  // element itself, or (options.link present) an `<a>` wrapper around it. Both
  // shapes are handled by checking the child itself first, then searching one
  // level in.
  _restorableTargetFor(child) {
    if (child.matches(RESTORABLE_ELEMENT_SELECTOR)) return child
    return child.querySelector(RESTORABLE_ELEMENT_SELECTOR)
  }

  // Resolve which editable element -- or, card #2295, which SLIDE, when the
  // click landed on empty slide space with no element under the cursor --
  // was right-clicked. The action is wired (via DecksHelper#deck_element_edit_attrs,
  // #deck_element_menu_image_attrs, and the slide wrapper's own fallback
  // action in decks/_viewer_chrome.html.erb) onto elements/wrappers that
  // already carry data-slide-index (and, for a specific element,
  // data-element-index too), so event.currentTarget IS the right element —
  // mirrors deck_show_editor_controller.js's own former guard. Only
  // slideIndex is REQUIRED; elementIndex is left `undefined` on the slide
  // wrapper's fallback action, and stays undefined here rather than being
  // coerced to a placeholder value, so `frameSrc` can tell the two cases
  // apart. `composing` resets on every fresh open so a later right-click
  // always starts back at the menu, never mid-composer. The click point is
  // stashed too — see handleFrameLoad, which re-clamps position against
  // whatever actually loads (the menu or the taller composer), not just what
  // was showing (the tiny "Loading…" spinner) at the moment of the click.
  beforeOpen(event) {
    const target = event.currentTarget
    const { slideIndex, elementIndex } = target.dataset
    if (slideIndex === undefined) return false

    this.currentTarget = target
    this.currentSlideIndex = slideIndex
    this.currentElementIndex = elementIndex
    this.composing = false
    this._lastClickX = event.clientX
    this._lastClickY = event.clientY
    return true
  }

  // Same frame, two possible renders (DecksController#element_menu): the
  // menu by default, or the feedback composer when `composing` is set by
  // #addFeedback below. Swapping src on the SAME frame reuses the base's
  // loadFrame/loading-swap machinery rather than a second floating element.
  // The element segment is OPTIONAL (card #2295) -- an empty-slide right-
  // click leaves currentElementIndex undefined, matching the route's
  // optional :element_index segment (element/:slide_index(/:element_index)/menu).
  frameSrc() {
    const base = `/decks/${this.deckIdValue}/element/${this.currentSlideIndex}`
    const withElement = this.currentElementIndex !== undefined ? `${base}/${this.currentElementIndex}` : base
    return this.composing ? `${withElement}/menu?compose=1` : `${withElement}/menu`
  }

  // Re-clamp position on EVERY frame load (base class only positions once, in
  // open(), against whatever the "Loading…" spinner measured) — the menu and
  // the composer render at different heights, and a right-click near the
  // bottom or right edge of the slide needs the box re-checked against the
  // viewport once its REAL size is known, not just the initial guess.
  // Composer-only wiring (draft restore/persist, stricter dismissal) happens
  // here too, once its markup actually exists in the DOM.
  handleFrameLoad() {
    super.handleFrameLoad()

    if (this._lastClickX !== undefined) this.positionMenu(this._lastClickX, this._lastClickY)
    if (this.composing) this._wireComposer()
  }

  edit(event) {
    event.preventDefault()
    const target = this.currentTarget
    this.close()

    const editor = this.application.getControllerForElementAndIdentifier(this.element, "deck-show-editor")
    editor?.openEditorFor(target)
  }

  addFeedback(event) {
    event.preventDefault()
    this.composing = true
    this._capturePinPosition()
    this.showLoading()
    this.loadFrame()
  }

  // Converts the ORIGINAL right-click point (stashed in beforeOpen, in
  // viewport/screen pixels) into a percentage of the slide canvas's own
  // rendered box — not the "Add Feedback" menu item's position, which is
  // somewhere else entirely inside the floating menu. Percentage (not raw
  // pixels) so the stored point is independent of the viewer's current CSS
  // scale (.airdrop-slide-scale's transform) — decks/_slide_feedback_pins.html.erb
  // renders it back the same way every other canvas element is positioned
  // (left/top as a fraction of the slide box), so it lines up regardless of
  // zoom level or screen size. Both null when the click point can't be
  // resolved (e.g. a malformed DOM) — the server treats that exactly like a
  // pre-feature comment: no pin, nothing else affected.
  //
  // TWO SHAPES for where `.airdrop-slide` sits relative to currentTarget
  // (bug found live, card #2295 widening): a right-click on an ELEMENT
  // (image/headline/text/html, including the image crop wrapper) has
  // `.airdrop-slide` as an ANCESTOR — decks/slides/_canvas.html.erb renders
  // every element as a direct child of it — so `.closest()` finds it walking
  // up. A right-click on genuinely EMPTY slide space fires the FALLBACK
  // action wired onto the slide's own OUTER WRAPPER
  // (`.airdrop-viewer__slide` in decks/_viewer_chrome.html.erb), and
  // `.airdrop-slide` sits INSIDE that wrapper (nested a few levels down,
  // through `.relative` > `.airdrop-slide-scale`) — a DESCENDANT, which
  // `.closest()` can never find searching upward. `.closest()` first (the
  // common, pre-#2295 element case), falling back to `.querySelector()` (the
  // wrapper case) covers both directions without needing to know which one
  // triggered this call.
  _capturePinPosition() {
    const slideEl = this.currentTarget?.closest(".airdrop-slide") ?? this.currentTarget?.querySelector(".airdrop-slide")
    if (!slideEl || this._lastClickX === undefined) {
      this._pinXPct = null
      this._pinYPct = null
      this._elementRef = null
      this._elementContentSnapshot = null
      return
    }

    const rect = slideEl.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) {
      this._pinXPct = null
      this._pinYPct = null
      this._elementRef = null
      this._elementContentSnapshot = null
      return
    }

    this._pinXPct = ((this._lastClickX - rect.left) / rect.width) * 100
    this._pinYPct = ((this._lastClickY - rect.top) / rect.height) * 100

    // Element attribution (card #2378, deck #42 Yarra Valley Hilltop incident
    // — a 9-stat-tile slide with three ambiguous comments, none saying which
    // tile). Resolved from the SAME click this method just placed the pin
    // at — deliberately not a second, separately-timed capture, so the
    // element this records can never drift from where the dot actually
    // renders.
    this._captureElementSnapshot()
  }

  // Resolves the nearest slide element to the click that just placed the pin,
  // and snapshots its content at that moment — both travel with the comment
  // (card #2378's extension) because the element's own content can change
  // after the fact: a stat tile reading "580" when a reviewer writes "This
  // needs to be 620" may show a different number by the time anyone reads
  // the comment, so the element reference alone isn't enough.
  //
  // this.currentTarget is normally (or is inside) the right-clicked element
  // already — canvas elements carry data-element-id/data-element-index
  // whenever `editable` (DecksHelper#deck_element_edit_attrs) — so
  // `.closest()` resolves it directly for the common case: a right-click
  // landing ON an element. A right-click on genuinely empty slide space
  // fires the fallback action wired onto the slide's own outer wrapper (see
  // _capturePinPosition's header comment on the two DOM shapes), which
  // carries neither attribute, so `.closest()` correctly finds nothing there
  // and this falls through to `document.elementFromPoint` at the captured
  // click coordinates — nudged a small radius in each direction (a few
  // pixels) so a near-miss right next to a tile's edge still resolves,
  // rather than only the exact pixel clicked. Nothing found even then (the
  // click was genuinely out in empty space) leaves both fields null — the
  // same graceful-null pattern _capturePinPosition already uses for a
  // malformed DOM, never a placeholder guess.
  _captureElementSnapshot() {
    this._elementRef = null
    this._elementContentSnapshot = null

    const NEAR_RADIUS_PX = 24
    const selector = "[data-element-id], [data-element-index]"
    let el = this.currentTarget?.closest(selector) ?? null

    if (!el && this._lastClickX !== undefined) {
      const offsets = [
        [0, 0],
        [NEAR_RADIUS_PX, 0], [-NEAR_RADIUS_PX, 0],
        [0, NEAR_RADIUS_PX], [0, -NEAR_RADIUS_PX],
      ]
      for (const [dx, dy] of offsets) {
        const found = document.elementFromPoint(this._lastClickX + dx, this._lastClickY + dy)
        el = found?.closest(selector) ?? null
        if (el) break
      }
    }

    if (!el) return

    const { elementId, elementIndex } = el.dataset
    if (elementId) {
      this._elementRef = elementId
    } else if (elementIndex !== undefined) {
      this._elementRef = `index:${elementIndex}`
    }

    // Whitespace-normalised (multi-line stat tiles render value + label as
    // separate child divs, so raw textContent carries the newline/indent
    // between them) — a readable snapshot is the point, not a literal
    // character-for-character DOM dump. Truncated to a sane length in case
    // the nearest element is a large text block.
    const content = (el.textContent || "").replace(/\s+/g, " ").trim()
    if (content) this._elementContentSnapshot = content.slice(0, 200)
  }

  // Cancel AND the composer's own "X" both land here — an explicit, deliberate
  // dismissal. Never clears the localStorage draft (only a successful post
  // does — see postFeedback): a cancelled comment should still be there next
  // time this slide's composer is opened.
  cancelFeedback(event) {
    event.preventDefault()
    this.close()
  }

  // Posts the composer's textarea straight into DeckSlideFeedbacksController
  // #create, scoped to the slide the right-clicked element belongs to (slides
  // carry the only granularity this model supports — see DeckSlideFeedback's
  // header comment). A blank body is never posted (mirrors every other
  // blank-content guard in this feature — nothing meaningful to save).
  async postFeedback(event) {
    event.preventDefault()
    const button = event.currentTarget
    const slideUuid = button.dataset.slideUuid
    const body = this.hasFeedbackBodyTarget ? this.feedbackBodyTarget.value.trim() : ""
    if (!body) return

    const target = this.currentTarget
    button.disabled = true

    try {
      const response = await fetch(`/decks/${this.deckIdValue}/slide_feedbacks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this._csrfToken(),
        },
        body: JSON.stringify({
          slide_uuid: slideUuid,
          body,
          position_x_pct: this._pinXPct,
          position_y_pct: this._pinYPct,
          element_ref: this._elementRef,
          element_content_snapshot: this._elementContentSnapshot,
        }),
      })

      if (!response.ok) throw new Error(`feedback post failed: ${response.status}`)

      Turbo.renderStreamMessage(await response.text())
      // Only a SUCCESSFUL post clears the draft — Cancel/Escape/a stray
      // dismissal must leave it recoverable next time this slide's composer
      // opens (see _wireComposer's restore-on-load).
      this._clearDraft(slideUuid)
      this.close()
      this._flash(target)
    } catch (error) {
      button.disabled = false
    }
  }

  // Wires the composer once its markup actually exists (called from
  // handleFrameLoad, composing-only): restores any saved draft, persists on
  // every keystroke, and switches this popover onto a STRICTER dismissal rule
  // than the plain menu's — a composer holding unsaved text must never vanish
  // from an accidental stray click or a right-click landing elsewhere. Only
  // Escape (context_menu_controller.js's keydown listener, left attached) or
  // the explicit Cancel/X button (#cancelFeedback, wired in the composer
  // markup) may close it from here on.
  _wireComposer() {
    this.disableClickAwayDismiss()

    if (!this.hasFeedbackBodyTarget) return

    const slideUuid = this.feedbackBodyTarget.dataset.slideUuid
    const draft = this._loadDraft(slideUuid)
    if (draft) this.feedbackBodyTarget.value = draft

    this.feedbackBodyTarget.addEventListener("input", () => {
      this._saveDraft(slideUuid, this.feedbackBodyTarget.value)
    })
    this.feedbackBodyTarget.focus()
  }

  // Removes ONLY the base primitive's click-outside / right-click-elsewhere
  // dismissal (context_menu_controller.js#addListeners) by detaching its OWN
  // bound listener references — not a parallel listener system, a pure
  // subtraction. Escape (boundHandleKeydown) is deliberately left attached:
  // Escape closing a draft composer is expected and fine, an accidental
  // stray click discarding it silently is not.
  disableClickAwayDismiss() {
    if (this.boundClickOutside) {
      document.removeEventListener("click", this.boundClickOutside)
      this.boundClickOutside = null
    }
    document.removeEventListener("contextmenu", this.boundClose)
  }

  // localStorage draft persistence (deck #28 incident follow-up, Jay's live
  // test feedback) — keyed per (deck, slide), matching DeckSlideFeedback's
  // own slide-only granularity (see that model's header comment for why it
  // has no per-element concept) rather than inventing a finer key the model
  // can't actually address. A large chunk of typed feedback then survives an
  // accidental dismissal, a reload, or navigating away and back. Storage
  // access is wrapped defensively — a draft is a convenience, never a
  // requirement, so private-browsing/quota errors fail silently rather than
  // breaking the composer.
  _draftKey(slideUuid) {
    return `deck-${this.deckIdValue}-slide-feedback-draft-${slideUuid}`
  }

  _saveDraft(slideUuid, value) {
    if (!slideUuid) return
    try {
      localStorage.setItem(this._draftKey(slideUuid), value)
    } catch (_error) {
      // Ignore — see header comment above.
    }
  }

  _loadDraft(slideUuid) {
    if (!slideUuid) return ""
    try {
      return localStorage.getItem(this._draftKey(slideUuid)) || ""
    } catch (_error) {
      return ""
    }
  }

  _clearDraft(slideUuid) {
    if (!slideUuid) return
    try {
      localStorage.removeItem(this._draftKey(slideUuid))
    } catch (_error) {
      // Ignore — see header comment above.
    }
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  // Reuses deck_show_editor_controller.js's exact save-confirmation cue (same
  // class/keyframes in application.css) — a brief ring pulse on the element
  // the feedback was about, so posting feedback gets the same visible
  // confirmation an edit does.
  _flash(target) {
    const className = "deck-show-editor-flash-success"
    target.classList.remove(className)
    void target.offsetWidth // force reflow so a rapid repeat commit restarts the animation
    target.classList.add(className)
    target.addEventListener("animationend", () => target.classList.remove(className), { once: true })
  }
}
